// src/components/Seller/SellerInventory.js
import React, { useEffect, useState } from "react";
import "../../styles/SellerInventory.css";
import config from "../../config";

export default function SellerInventory({ sellerId }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [updating, setUpdating] = useState(false);

  // inline editor state
  const [inlineEdit, setInlineEdit] = useState(null);
  // inlineEdit = { variantId, currentStock, quantity: "", isIncrease: true }

  // 🔐 Same pattern as SellerProducts – use seller_id_token
  const token = localStorage.getItem("seller_id_token");

  const showSuccess = (message) => {
    setSuccess(message);
    // auto-hide after 3 seconds
    setTimeout(() => {
      setSuccess("");
    }, 3000);
  };

  // ---------------------- FORMATTERS ----------------------
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (num) => {
    if (num == null || isNaN(num)) return "-";
    return `₹${parseFloat(num).toLocaleString("en-IN")}`;
  };

  // ---------------------- LOAD INVENTORY ----------------------
  const fetchInventory = async () => {
    if (!sellerId) return;

    setLoading(true);
    setError("");

    try {
      const headers = {
        Accept: "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // --- SUMMARY ---
      const summaryRes = await fetch(
        `${config.baseUrl}/api/seller/inventory/${sellerId}/summary`,
        { headers }
      );

      if (!summaryRes.ok) {
        const body = await summaryRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load inventory summary");
      }

      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // --- ITEMS ---
      const itemsRes = await fetch(
        `${config.baseUrl}/api/seller/inventory/${sellerId}/items`,
        { headers }
      );

      if (!itemsRes.ok) {
        const body = await itemsRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load inventory items");
      }

      const itemsData = await itemsRes.json();
      setItems(Array.isArray(itemsData) ? itemsData : []);
    } catch (err) {
      console.error("Seller inventory fetch error:", err);
      setError(err.message || "Unable to load seller inventory");
      setSummary(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  // ---------------------- STOCK UPDATE (PATCH) ----------------------
  const adjustStock = async (variantId, delta, reason) => {
    if (!sellerId || !variantId) return;

    if (
      !window.confirm(
        `${delta > 0 ? "Increase" : "Decrease"} stock by ${Math.abs(
          delta
        )} for variant ${variantId}?`
      )
    ) {
      return;
    }

    try {
      setUpdating(true);
      setError("");
      setSuccess("");

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(
        `${config.baseUrl}/api/seller/inventory/${sellerId}/items/${variantId}/stock`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            quantity: Math.abs(delta),
            isIncrease: delta > 0,
            reason:
              reason ||
              (delta > 0
                ? "Increase from seller portal"
                : "Decrease from seller portal"),
            notes: "Updated from AuraKart Seller Portal",
          }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Stock update failed");
      }

      // refresh after successful update
      await fetchInventory();
      showSuccess("Stock updated successfully.");
    } catch (err) {
      console.error("Stock update error:", err);
      setError(err.message || "Unable to update stock");
      setSuccess("");
    } finally {
      setUpdating(false);
    }
  };

  // ---------------------- INLINE EDIT HANDLERS ----------------------
  const openInlineEdit = (item) => {
    setInlineEdit({
      variantId: item.variantId,
      currentStock: item.currentStock,
      quantity: "",
      isIncrease: true,
    });
  };

  const cancelInlineEdit = () => {
    setInlineEdit(null);
  };

  const handleInlineQuantityChange = (value) => {
    if (value === "" || /^\d*$/.test(value)) {
      setInlineEdit((prev) => (prev ? { ...prev, quantity: value } : prev));
    }
  };

  const handleInlineDirectionChange = (isIncrease) => {
    setInlineEdit((prev) => (prev ? { ...prev, isIncrease } : prev));
  };

  const handleInlineUpdate = async () => {
    if (!inlineEdit) return;

    const qty = parseInt(inlineEdit.quantity, 10);
    if (!qty || Number.isNaN(qty) || qty <= 0) {
      alert("Please enter a positive quantity.");
      return;
    }

    const delta = inlineEdit.isIncrease ? qty : -qty;

    await adjustStock(
      inlineEdit.variantId,
      delta,
      `${inlineEdit.isIncrease ? "Increase" : "Decrease"} stock by ${qty} from inline editor`
    );

    setInlineEdit(null);
  };

  // ---------------------- RENDER STATES ----------------------
  if (!sellerId) {
    return (
      <div className="inv-container">
        <div className="inv-header">
          <h2>📦 Inventory</h2>
          <p>Seller ID is missing.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="inv-container">
        <div className="inv-header">
          <h2>📦 Inventory</h2>
        </div>
        <p className="loading">Loading your inventory…</p>
      </div>
    );
  }

  if (error && !summary && items.length === 0) {
    return (
      <div className="inv-container">
        <div className="inv-header">
          <h2>📦 Inventory</h2>
        </div>
        <p className="error">Error: {error}</p>
      </div>
    );
  }

  // ---------------------- MAIN UI ----------------------
  return (
    <div className="inv-container fade-in">
      <div className="inv-header">
        <div>
          <h2>📦 My Inventory</h2>
          <p>Variants and stock under your seller account</p>
        </div>
        <div className="inv-header-right">
          <span className="inv-seller-pill">
            Seller ID: <strong>{sellerId}</strong>
          </span>
          <button className="refresh-btn" onClick={fetchInventory}>
            ⟳ Refresh
          </button>
        </div>
      </div>

      {success && <p className="success">✅ {success}</p>}
      {error && <p className="error">⚠ {error}</p>}

      {/* SUMMARY CARDS */}
      {summary && (
        <div className="inv-summary">
          <div className="inv-card blue">
            <h3>{summary.totalItems ?? 0}</h3>
            <p>Total Variants</p>
          </div>

          <div className="inv-card yellow">
            <h3>{summary.totalStock ?? 0}</h3>
            <p>Total Stock</p>
          </div>

          <div className="inv-card red">
            <h3>{summary.lowStockItems ?? 0}</h3>
            <p>Low Stock Items</p>
          </div>

          <div className="inv-card green">
            <h3>{formatCurrency(summary.totalInventoryValue)}</h3>
            <p>Inventory Value</p>
          </div>
        </div>
      )}

      {/* META ROW */}
      <div className="inv-subcards">
        <span>Total Rows: {items.length}</span>
        <span>Low Stock: {summary?.lowStockItems ?? 0}</span>
        <span>Out of Stock: {summary?.outOfStockItems ?? 0}</span>
      </div>

      {/* TABLE */}
      <div className="inv-table-scroll">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Variant ID</th>
              <th>Product</th>
              <th>SKU</th>
              <th>Size</th>
              <th>Color</th>
              <th>Current Stock</th>
              <th>Offer Price</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={10} className="no-results">
                  No inventory items found
                </td>
              </tr>
            ) : (
              items.flatMap((item) => {
                const rows = [];

                // main row
                rows.push(
                  <tr key={item.variantId} className="row-anim">
                    <td>{item.variantId}</td>
                    <td className="full-text">{item.productName}</td>
                    <td className="full-text sku-text">{item.sku}</td>
                    <td>{item.sizeName}</td>
                    <td>{item.color || "-"}</td>
                    <td>{item.currentStock}</td>
                    <td>{formatCurrency(item.offerPrice)}</td>
                    <td>
                      <span
                        className={`inv-status ${
                          item.status === "IN_STOCK"
                            ? "green"
                            : item.status === "LOW_STOCK"
                            ? "yellow"
                            : "red"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td>{formatDate(item.lastUpdated)}</td>
                    <td>
                      <div className="inv-actions-main">
                        <button
                          className="inv-btn minus"
                          disabled={updating}
                          onClick={() =>
                            adjustStock(
                              item.variantId,
                              -1,
                              "Decrease stock by 1 from seller portal"
                            )
                          }
                        >
                          -1
                        </button>
                        <button
                          className="inv-btn plus"
                          disabled={updating}
                          onClick={() =>
                            adjustStock(
                              item.variantId,
                              1,
                              "Increase stock by 1 from seller portal"
                            )
                          }
                        >
                          +1
                        </button>
                        <button
                          className="inv-edit-link"
                          type="button"
                          disabled={updating}
                          onClick={() => openInlineEdit(item)}
                        >
                          ✏ Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );

                // inline edit row
                if (inlineEdit && inlineEdit.variantId === item.variantId) {
                  rows.push(
                    <tr
                      key={`${item.variantId}-inline`}
                      className="inv-inline-row"
                    >
                      <td colSpan={10}>
                        <div className="inv-inline-editor">
                          <div className="inv-inline-fields">
                            <div className="inv-inline-field">
                              <label>Current Stock:</label>
                              <div className="inv-inline-value">
                                {inlineEdit.currentStock}
                              </div>
                            </div>

                            <div className="inv-inline-field">
                              <label>Action:</label>
                              <div className="inv-inline-toggle">
                                <button
                                  type="button"
                                  className={`inv-toggle-btn ${
                                    inlineEdit.isIncrease ? "active" : ""
                                  }`}
                                  onClick={() =>
                                    handleInlineDirectionChange(true)
                                  }
                                >
                                  Increase
                                </button>
                                <button
                                  type="button"
                                  className={`inv-toggle-btn ${
                                    !inlineEdit.isIncrease ? "active" : ""
                                  }`}
                                  onClick={() =>
                                    handleInlineDirectionChange(false)
                                  }
                                >
                                  Decrease
                                </button>
                              </div>
                            </div>

                            <div className="inv-inline-field">
                              <label>
                                {inlineEdit.isIncrease
                                  ? "Stock Increase By:"
                                  : "Stock Decrease By:"}
                              </label>
                              <input
                                type="number"
                                min="1"
                                className="inv-inline-input"
                                placeholder="Enter quantity"
                                value={inlineEdit.quantity}
                                onChange={(e) =>
                                  handleInlineQuantityChange(e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="inv-inline-actions">
                            <button
                              type="button"
                              className="inv-inline-save"
                              disabled={updating}
                              onClick={handleInlineUpdate}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="inv-inline-cancel"
                              onClick={cancelInlineEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return rows;
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
